import type { SkipToken, SuspenseQueryHookOptions } from "@apollo/client";
import { DocumentNode, OperationVariables, skipToken, TypedDocumentNode, useSuspenseQuery } from "@apollo/client";
import { useBrowserStatus } from "@sinch/core/providers/BrowserStatusProvider";

/**
 * Load data from server and return loading and error statuses
 * See https://www.apollographql.com/docs/react/api/react/hooks#usequery
 */
export function useSuspenseQueryHandler<
  TData = Record<string, unknown>,
  TVariables extends OperationVariables = OperationVariables,
>(
  query: DocumentNode | TypedDocumentNode<TData, TVariables>,
  variables?: TVariables,
  options: Omit<SuspenseQueryHookOptions<TData, TVariables>, "variables"> | SkipToken = {}
) {
  const { error, ...queryReturnProps } = useSuspenseQuery<TData, TVariables>(
    query,
    options === skipToken
      ? skipToken
      : {
          ...(variables ? { variables } : {}),
          ...options,
        }
  );
  const { pageStatus } = useBrowserStatus();

  if (pageStatus !== "unloading" && error) {
    throw error;
  }

  return { error, ...queryReturnProps };
}
